export type TierId = 'economy' | 'professional' | 'expert';

export interface TierDefinition {
    id: TierId;
    name: string;
    factor: number;
    quality: string;
    targetingProfile?: string;
    popular?: boolean;
    features: string[];
    color: string;
}

// NOTE: 'economy' tier is RETIRED from sale (no longer shown in purchase UI),
// but kept in PRICING_MATRIX so ~3,170 legacy economy projects keep calculating.
// Only Professional + Expert are offered to new buyers.
export const TIERS: TierDefinition[] = [
    {
        id: 'professional',
        name: 'Professional',
        factor: 1.0,
        quality: 'Residential IPs',
        targetingProfile: 'Residential Targeting Profile',
        popular: true,
        features: ['Residential IP Network', 'Higher Trust Traffic Quality', 'Country-Level Targeting', 'Credits Never Expire'],
        color: 'border-orange-300'
    },
    {
        id: 'expert',
        name: 'Expert',
        factor: 1.4,
        quality: 'Premium IPs',
        targetingProfile: 'Premium Targeting Profile',
        features: ['Premium Residential Network', 'GA4 Natural Events', 'City & State Targeting', 'Automatic Website Crawler'],
        color: 'border-[#ff5a0a]'
    }
];

// Legacy economy tier definition (NOT in TIERS — used only for rendering legacy projects)
export const LEGACY_ECONOMY: TierDefinition = {
    id: 'economy',
    name: 'Economy',
    factor: 0.5,
    quality: 'Datacenter IPs',
    targetingProfile: 'Country Targeting Profile',
    features: ['Datacenter IP Pool', 'High-Volume Delivery', 'Country-Level Targeting', 'Best Price per Visitor'],
    color: 'border-gray-200'
};

// New unified volume steps (9 tiers, round market-anchored numbers)
export const VOLUME_STEPS = [60000, 300000, 600000, 1000000, 3000000, 6000000, 10000000, 30000000, 60000000] as const;

// Legacy steps for old economy projects (do not use for new purchases)
export const LEGACY_VOLUME_STEPS = [70000, 600000, 1200000, 12000000, 60000000] as const;

export const BULK_OPTIONS = [1, 12, 24] as const;

export const MIN_VOLUME = VOLUME_STEPS[0];
export const MAX_VOLUME = VOLUME_STEPS[VOLUME_STEPS.length - 1];

export const PRICING_MATRIX: Record<TierId, Record<number, Record<number, number>>> = {
    // RETIRED from sale — kept for legacy economy projects only
    economy: {
        70000: { 1: 19.95, 12: 199.50, 24: 379.05 },
        600000: { 1: 57.95, 12: 579.50, 24: 1101.05 },
        1200000: { 1: 99.95, 12: 999.50, 24: 1899.05 },
        12000000: { 1: 699.95, 12: 6999.50, 24: 13299.05 },
        60000000: { 1: 2799.95, 12: 27999.50, 24: 53199.05 },
    },
    professional: {
        60000:    { 1: 19.95,   12: 199.50,    24: 379.05 },
        300000:   { 1: 59.95,   12: 599.50,    24: 1139.05 },
        600000:   { 1: 114.95,  12: 1149.50,   24: 2184.05 },
        1000000:  { 1: 189.95,  12: 1899.50,   24: 3609.05 },
        3000000:  { 1: 489.95,  12: 4899.50,   24: 9309.05 },
        6000000:  { 1: 849.95,  12: 8499.50,   24: 16149.05 },
        10000000: { 1: 1299.95, 12: 12999.50,  24: 24699.05 },
        30000000: { 1: 3299.95, 12: 32999.50,  24: 62699.05 },
        60000000: { 1: 5399.95, 12: 53999.50,  24: 102599.05 },
    },
    expert: {
        60000:    { 1: 27.95,   12: 279.50,    24: 531.05 },
        300000:   { 1: 83.95,   12: 839.50,    24: 1595.05 },
        600000:   { 1: 160.95,  12: 1609.50,   24: 3058.05 },
        1000000:  { 1: 265.95,  12: 2659.50,   24: 5053.05 },
        3000000:  { 1: 685.95,  12: 6859.50,   24: 13033.05 },
        6000000:  { 1: 1189.95, 12: 11899.50,  24: 22609.05 },
        10000000: { 1: 1819.95, 12: 18199.50,  24: 34579.05 },
        30000000: { 1: 4619.95, 12: 46199.50,  24: 87779.05 },
        60000000: { 1: 7559.95, 12: 75599.50,  24: 143639.05 },
    }
};

export const PUBLIC_STARTER_OFFER = Object.freeze({
    tier: 'professional' as const,
    volume: VOLUME_STEPS[0],
    price: PRICING_MATRIX.professional[VOLUME_STEPS[0]][1],
    currency: 'USD' as const,
    cpm: (PRICING_MATRIX.professional[VOLUME_STEPS[0]][1] / VOLUME_STEPS[0]) * 1000,
});

export const PUBLIC_MAX_VOLUME_SAVINGS_PERCENT = Math.round(
    (1 - (
        PRICING_MATRIX.professional[VOLUME_STEPS[0]][24]
        / (PRICING_MATRIX.professional[VOLUME_STEPS[0]][1] * 24)
    )) * 1000,
) / 10;

/**
 * Programmatic landing pages include older localized copy that predates the
 * current Professional/Expert matrix. Keep those pages indexable without ever
 * publishing retired Economy pricing or obsolete per-1,000 claims.
 */
export const normalizePublicPricingCopy = (value: string): string => {
    const currentOffer = `$${PUBLIC_STARTER_OFFER.price.toFixed(2)} for ${PUBLIC_STARTER_OFFER.volume.toLocaleString('en-US')} visitors`;

    return value
        .replace(
            /(?:€\s*)?0[.,]17\s*(?:€|EUR|euros?|euro|евро|ユーロ|유로|欧元)?(?:\s*(?:CPM|\/\s*1(?:[,.\s]?000|k)))?/giu,
            currentOffer,
        )
        .replace(
            /(?:\$\s*)?2[.,]40\s*(?:\$|USD|dollars?|dollar|доллар(?:а|ов)?|ดอลลาร์|美元)?(?:\s*(?:\/|per|por|pro|par|за|每|ต่อ|cho)\s*1(?:[,.\s]?000|k))?/giu,
            currentOffer,
        )
        .replace(
            /(?:\$\s*)?1[.,]50\s*(?:\$|USD|dollars?|dollar|доллар(?:а|ов)?|ดอลลาร์|美元)?(?:\s*(?:\/|per|por|pro|par|за|每|ต่อ|cho)\s*1(?:[,.\s]?000|k))?/giu,
            currentOffer,
        )
        .replace(/Economy tier/giu, 'Professional tier')
        .replace(/6-pack\s*\(20% off\)\s*and\s*24-pack\s*\(40% off\)\s*bulk discounts/giu, '12-pack and 24-pack volume options')
        .replace(/24-pack bulk discount/giu, '24-pack volume option')
        .replace(/(?:volume\s+)?discounts up to 40%/giu, `volume savings up to ${PUBLIC_MAX_VOLUME_SAVINGS_PERCENT}%`)
        .replace(/20\s*%\s*(?:-|–|—|~|to|bis|a|à|até|до|至|から|〜)\s*40\s*%/giu, `up to ${PUBLIC_MAX_VOLUME_SAVINGS_PERCENT}%`)
        .replace(/40\s*%/gu, `${PUBLIC_MAX_VOLUME_SAVINGS_PERCENT}%`)
        .replace(/\s{2,}/g, ' ')
        .trim();
};

// ANOMALY FIX: prices come from an EXACT lookup on defined steps — no runtime
// interpolation (the old anchorPrice*(v/anchor) caused e.g. 11M > 12M).
// Volume must be one of VOLUME_STEPS (slider snaps to them). Legacy economy
// volumes resolve against the economy matrix keys.
export const getTierPrice = (tierId: TierId, volume: number, bulk: number = 1): number => {
    const tierPrices = PRICING_MATRIX[tierId];
    if (!tierPrices) return 0;
    const step = tierPrices[volume];
    if (!step) return 0;
    return step[bulk] ?? step[1] ?? 0;
};

export const getCPM = (tierId: TierId, volume: number, bulk: number = 1): number => {
    const price = getTierPrice(tierId, volume, bulk);
    const totalVisitors = volume * bulk;
    if (totalVisitors === 0) return 0;
    return (price / totalVisitors) * 1000;
};

// Kept for backwards-compat; with snap-to-step it returns the exact volume.
export const getAnchorVolume = (volume: number): number => {
    let anchorVolume: number = MIN_VOLUME;
    for (const step of VOLUME_STEPS) {
        if (volume < step) break;
        anchorVolume = step;
    }
    return anchorVolume;
};

export const formatPrice = (price: number, currency: string = 'USD'): string => {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency }).format(price);
};

export const formatCPM = (cpm: number): string => {
    return `$${cpm.toFixed(2)}`;
};

export const formatVolume = (volume: number): string => {
    if (volume >= 1000000) {
        return `${(volume / 1000000).toFixed(volume % 1000000 === 0 ? 0 : 1)}M`;
    }
    if (volume >= 1000) {
        return `${(volume / 1000).toFixed(volume % 1000 === 0 ? 0 : 0)}k`;
    }
    return volume.toString();
};

export const SUBSCRIPTION_PLANS = [
    {
        id: 'ignite',
        name: 'Ignite',
        price: 19.99,
        yearlyPrice: 199.90, // 2 months free
        visitors: 25000,
        pageviews: 225000,
        priceId: 'price_ignite_monthly',
        yearlyPriceId: 'price_ignite_yearly',
        features: ['25,000 Guaranteed Visitors', 'Up to 225k Pageviews', 'Core Engine', 'Traffic Rewards']
    },
    {
        id: 'momentum',
        name: 'Momentum',
        price: 49.00,
        yearlyPrice: 490.00,
        visitors: 100000,
        pageviews: 1000000,
        priceId: 'price_momentum_monthly',
        yearlyPriceId: 'price_momentum_yearly',
        popular: true,
        features: ['100,000 Guaranteed Visitors', 'Up to 1m Pageviews', 'Core Engine', 'Traffic Rewards']
    },
    {
        id: 'breakthrough',
        name: 'Breakthrough',
        price: 99.00,
        yearlyPrice: 990.00,
        visitors: 300000,
        pageviews: 3000000,
        priceId: 'price_breakthrough_monthly',
        yearlyPriceId: 'price_breakthrough_yearly',
        features: ['300,000 Guaranteed Visitors', 'Up to 3m Pageviews', 'Core Engine', 'Traffic Rewards', 'Custom Reports']
    },
    {
        id: 'scale',
        name: 'Scale',
        price: 249.00,
        yearlyPrice: 2490.00,
        visitors: 1000000,
        pageviews: 15000000,
        priceId: 'price_scale_monthly',
        yearlyPriceId: 'price_scale_yearly',
        features: ['1m Guaranteed Visitors', 'Up to 15m Pageviews', 'Core Engine', 'Traffic Rewards', 'Custom Reports', 'API Access', 'Premium Geo Controls', 'Custom Events', 'Dedicated Account Manager']
    },
    {
        id: 'agency_pro',
        name: 'Agency Pro',
        price: 799.00,
        yearlyPrice: 7990.00,
        visitors: 10000000,
        pageviews: 150000000,
        priceId: 'price_agency_pro_monthly',
        yearlyPriceId: 'price_agency_pro_yearly',
        isAgency: true,
        features: ['10m Guaranteed Visitors', 'Up to 150m Pageviews', 'All Scale Features', 'Custom Distribution Pattern', 'Custom Feature Development']
    },
    {
        id: 'whitelabel_partner',
        name: 'Whitelabel Partner',
        price: 3000.00,
        yearlyPrice: 30000.00,
        visitors: 50000000,
        pageviews: 750000000,
        priceId: 'price_whitelabel_monthly',
        yearlyPriceId: 'price_whitelabel_yearly',
        isAgency: true,
        features: ['50m Guaranteed Visitors', 'Up to 750m Pageviews', 'All Agency Pro Features', 'On-request Whitelabel Website']
    }
];

export const CREDIT_PACKS = [
    { volume: 60000, label: '60k Visitors' },
    { volume: 300000, label: '300k Visitors' },
    { volume: 600000, label: '600k Visitors' },
    { volume: 1000000, label: '1M Visitors' },
    { volume: 3000000, label: '3M Visitors' },
    { volume: 6000000, label: '6M Visitors' },
    { volume: 10000000, label: '10M Visitors' },
    { volume: 30000000, label: '30M Visitors' },
    { volume: 60000000, label: '60M Visitors' }
];
